import React, { useEffect, useState, useMemo, useCallback } from "react";
import debounce from "lodash/debounce";

import Table from "../table/table.component";
import useAppContext from "../../../hooks/useAppContext";
import InputSearch from "../../inputSearch/inputSearch.component";

import { StyledWrapper } from "./tableWrapper.style";
import { tableConfig } from "./constants";
import usePaginate from "../../../hooks/usePaginate";

const TableWrapper = ({ id, mode, name }) => {
  const { axios } = useAppContext();
  const {
    paginationComponent,
    setCurrentLimit,
    setCurrentPage,
    setTotalCount,
    currentLimit,
    currentPage,
  } = usePaginate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [meta, setMeta] = useState(undefined);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const settings = useMemo(() => tableConfig[name], [name]);
  const { withSearch, url, withPagination } = settings;

  const fetchData = useCallback(
    (page, limit, search) => {
      setIsLoading(true);
      const payload = {
        id: id,
        mode: mode,
      };
      if (withPagination) {
        payload["page"] = page;
        payload["limit"] = limit;
      }
      if (search) {
        payload["search"] = search;
      }
      axios
        .get(url, {
          params: {
            ...payload,
          },
        })
        .then((res) => {
          setData(res.data.data);
          if (res.data?.meta) setMeta(res.data?.meta);
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [url, mode, withPagination],
  );

  useEffect(() => {
    if (!meta?.pagination) return;

    setCurrentLimit(meta?.pagination?.currentCount);
    setTotalCount(meta?.pagination?.totalCount);
  }, [meta, setTotalCount, setCurrentLimit, setCurrentPage]);

  useEffect(() => {
    fetchData(currentPage, currentLimit, debouncedSearch);
  }, [currentLimit, currentPage, fetchData, debouncedSearch]);

  const debouncedSearchSet = useCallback(
    debounce((val) => {
      setDebouncedSearch(val);
      setCurrentPage(1);
    }, 500),
    [setDebouncedSearch, setCurrentPage],
  );

  useEffect(() => debouncedSearchSet(search), [search, debouncedSearchSet]);
  const searchChangeHandler = (e) => setSearch(e.target.value);

  return (
    <StyledWrapper>
      {withSearch && (
        <InputSearch
          changeHandler={searchChangeHandler}
          searchValue={search}
          resetPressHandler={() => setSearch("")}
          customClassName="input-searchbar"
          placeholder={withSearch?.inputPlaceholder || "Search..."}
        />
      )}
      <Table data={data} isLoading={isLoading} mode={mode} {...settings} />
      {withPagination && paginationComponent}
    </StyledWrapper>
  );
};

export default TableWrapper;
